import MinimizedWidget from "@components/WAChatbutton/WAPreview/MinimizedWidget";
import WhatsappWidgetPreview from "@components/WAChatbutton/WAPreview/WAPreview";
import IntlPhoneInput from "@components/intl-phone-input";
import * as Sentry from "@sentry/nextjs";
import { checkStatus, parseJSON, setPostHeaders } from "@utils/fetch";
import Image from "next/image";
import React, { useEffect, useState, PropsWithChildren, useRef } from "react";
import { Controller, SubmitHandler, useFieldArray, useForm } from "react-hook-form";
import { FaMinus, FaPlus } from "react-icons/fa";
import { errMsgFocusStyle } from "@utils/form";
import FormLogo from "./FormLogo";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import ReCAPTCHA from "react-google-recaptcha";
import { insertWidgetDataInDB, WidgetFormType } from "database/supabase/freeTools";

const sitekey = process.env.NEXT_PUBLIC_API_RECAPTCHA_SITE_KEY || "";

export interface FormInputProps {
    wanumber: string;
    brandName: string;
    statusTag: string;
    logo: string;
    welcomeText: string;
    options?: {
        content: string;
    }[];
    question?: string;
    buttonPosition: string;
    triggerMsg: string;
    widgetStyle: string;
    customQuestion?: string;
    startChatBtnTxt?: string;
    widgetPositionMarginX: number;
    widgetPositionMarginY: number;
}

const baseSchema = z.object({
    wanumber: z
        .string()
        .min(1, "Phone number is required")
        .regex(/^\+\d{1,3}\s\d{6,14}$/, "Invalid phone number format"),
    brandName: z
        .string()
        .regex(/^(?!\s*$).+/, "Name is required")
        .min(3, "Minimum 3 characters are required")
        .max(128, "Maximum limit exceeded"),
    statusTag: z
        .string()
        .regex(/^(?!\s*$).+/, "Status tag is required")
        .min(3, "Minimum 3 characters are required")
        .max(128, "Maximum limit exceeded"),
    logo: z
        .string()
        .url("Invalid URL format")
        .regex(/\.(png|jpg|jpeg)$/i, "Logo must be a valid image URL (png, jpg, jpeg)")
        .max(1024, "Maximum limit exceeded"),
    welcomeText: z
        .string()
        .regex(/^(?!\s*$).+/, "Welcome Text is required")
        .min(3, "Minimum 3 characters are required")
        .max(140, "Maximum limit exceeded"),
    buttonPosition: z.string(),
    triggerMsg: z.string().max(26, "Maximum limit exceeded").optional(),
    widgetStyle: z.string(),
    widgetPositionMarginX: z
        .number({ invalid_type_error: "Enter any number between 0 and 1000" })
        .min(0, { message: "Value cannot be less than 0" })
        .max(1000, { message: "Value cannot be more than 1000" }),
    widgetPositionMarginY: z
        .number({ invalid_type_error: "Enter any number between 0 and 1000" })
        .min(0, { message: "Value cannot be less than 0" })
        .max(1000, { message: "Value cannot be more than 1000" }),
});
const getSchema = (version: string) => {
    if (version === "v1") {
        return baseSchema.extend({
            options: z
                .array(
                    z.object({
                        content: z.string().max(40, "Maximum limit exceeded").optional(),
                    })
                )
                .optional(),
            question: z.string().max(140, "Maximum limit exceeded").optional(),
        });
    } else {
        return baseSchema.extend({
            customQuestion: z
                .string()
                .regex(/^(?!\s*$).+/, "Custom question Text is required")
                .min(3, "Minimum 3 characters are required"),
            startChatBtnTxt: z
                .string()
                .regex(/^(?!\s*$).+/, "startChatBtnTxt is required")
                .min(3, "Minimum 3 characters are required")
                .max(30, "Maximum limit exceeded"),
        });
    }
};
export interface WidgetFormProps {
    onGenerate: (data: FormInputProps) => void;
    defaultValue?: Partial<FormInputProps> | null;
    buttonName?: string;
}

const optionButtonStyle: React.CSSProperties = {
    width: "25px",
    height: "25px",
    border: "1px solid #BEE3F8",
    borderRadius: "100%",
    color: "#4299E1",
    backgroundColor: "white",
    padding: "5px",
};
const inputStyle: React.CSSProperties = { padding: "10px", margin: "0" };

const optionsPlaceholder = [
    { id: 1, content: "I'd like to chat with sales" },
    { id: 2, content: "I have a support question" },
];

const WidgetForm: React.FC<WidgetFormProps> = (props) => {
    const { onGenerate, defaultValue, buttonName = "Generate Chat Widget Code" } = props;

    const [country, setCountry] = useState();
    const [recaptchaError, setRecaptchaError] = useState<string | null>(null);
    const [version, setVersion] = useState("v1");

    const recaptchaRef = useRef<ReCAPTCHA>(null);
    const phoneInputRef = useRef<HTMLInputElement | null>(null);
    const logoInputRef = useRef<HTMLInputElement | null>(null);
    const {
        register,
        handleSubmit,
        control,
        formState: { errors },
        reset,
        setValue,
        watch,
        clearErrors,
    } = useForm<FormInputProps>({
        resolver: zodResolver(getSchema(version)),
        defaultValues: defaultValue ?? {
            options: [{ content: "" }, { content: "" }],
            logo: "https://assets.gallabox.com/gb-home/wa-chat-widget/bot-logo-1.png",
            widgetPositionMarginX: 12,
            widgetPositionMarginY: 12,
        },
    });

    const { fields, append, remove } = useFieldArray({
        control,
        name: "options",
    });

    const addOptions = (index: number) => {
        optionsPlaceholder.push({
            id: index + 1,
            content: optionsPlaceholder[index].content,
        });
        append({
            content: "",
        });
    };

    const removeOptions = (index: number) => {
        remove(index);
        optionsPlaceholder.pop();
    };

    const onSubmit: SubmitHandler<FormInputProps> = async (data) => {
        if (errors.wanumber) {
            phoneInputRef.current?.focus();
            return;
        }
        if (errors.logo) {
            logoInputRef.current?.focus();
            return;
        }
        const recaptchaValue = await recaptchaRef.current?.executeAsync();
        if (!recaptchaValue) {
            setRecaptchaError("Please complete the ReCAPTCHA");
            return;
        }

        setRecaptchaError(null);
        recaptchaRef.current?.reset();
        onGenerate(data);
        window.scrollTo({
            top: 0,
            behavior: "smooth",
        });
        if (window.gtag) {
            window.gtag("event", "conversion", {
                send_to: "AW-451834661/RlTWCNDS-pACEKXmudcB",
            });
        }

        await fetch("/api/v1/sendCode", setPostHeaders({ data }))
            .then(checkStatus)
            .then(parseJSON)
            .catch((error) => {
                Sentry.captureException(error);
            });

        const { buttonPosition, options, ...restData } = data;

        const payload: WidgetFormType = {
            ...restData,
            replyOptions: options?.map((item) => item?.content),
            widgetPosition: buttonPosition,
        };

        insertWidgetDataInDB(payload);
    };

    const selectedWidgetStyle = watch("widgetStyle", "v1");
    const selectedWidgetPosition = watch("buttonPosition", "LEFT");

    useEffect(() => {
        if (defaultValue) {
            reset(defaultValue);
        }
        setValue("logo", "https://assets.gallabox.com/gb-home/wa-chat-widget/bot-logo-1.png");
        setValue("options", [{ content: "" }, { content: "" }]);
        setValue("welcomeText", "");
        setValue("question", "");
        setValue("customQuestion", "");
        setValue("startChatBtnTxt", "");
        setValue("triggerMsg", "");
        clearErrors(["logo", "options", "welcomeText", "question", "customQuestion", "startChatBtnTxt", "triggerMsg"]);
    }, [defaultValue, selectedWidgetStyle, reset, setValue, clearErrors]);

    useEffect(() => {
        fetch("https://www.iplocate.io/api/lookup/json")
            .then(checkStatus)
            .then(parseJSON)
            .then((response) => setCountry(response.country_code));
    }, []);

    return (
        <div className="flex lg:flex-row flex-col justify-center gap-16 max-w-6xl lg:mx-auto mx-2">
            <div className="lg:w-1/2 w-full h-fit  pb-3 border-b border-solid border-gray-100 rounded-xl bg-white shadow-xl">
                <form onSubmit={handleSubmit(onSubmit)} id="gb_chatWidgetGenerator_form">
                    <div className="p-6 flex flex-col gap-10 border-b border-solid border-gray-100">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center">
                                <Image height={50} width={50} src="https://assets.gallabox.com/gb-home/wa-chat-widget/phone-icon.png" alt="WALogo" />
                                <span className="ml-2 text-2xl font-bold text-gray-700">Chat Widget Constructor</span>
                            </div>
                        </div>
                        <div className="flex flex-col gap-8">
                            <div className="flex flex-col gap-2">
                                <WidgetFormLabel htmlFor="WANumber">
                                    Enter your WhatsApp number <span className="text-red-400">*</span>
                                </WidgetFormLabel>
                                <Controller
                                    name="wanumber"
                                    control={control}
                                    render={({ field }) => {
                                        const countryCodeAndPhoneNumber = field.value?.split(" ") ?? [];
                                        return (
                                            <IntlPhoneInput
                                                style={{ marginBottom: "0px", paddingLeft: "70px", ...(errors.wanumber?.message ? errMsgFocusStyle : {}) }}
                                                className="text-base font-normal"
                                                country={country}
                                                placeholder="Your phone number"
                                                inputRef={(ref: HTMLInputElement) => {
                                                    field.ref(ref);
                                                    phoneInputRef.current = ref;
                                                }}
                                                {...field}
                                                value={countryCodeAndPhoneNumber.length > 1 ? countryCodeAndPhoneNumber?.[1] : field.value}
                                            />
                                        );
                                    }}
                                />
                                {!errors.wanumber?.message ? (
                                    <span className="text-sm font-normal text-gray-500">Note: Check if the entered country code is correct.</span>
                                ) : (
                                    <FormErrorMessage>{errors.wanumber.message}</FormErrorMessage>
                                )}
                            </div>
                            <div className="flex flex-col gap-2">
                                <WidgetFormLabel htmlFor="name">
                                    Name of your business/brand <span className="text-red-400">*</span>
                                </WidgetFormLabel>
                                <input
                                    style={{ ...inputStyle, ...(errors.brandName?.message ? errMsgFocusStyle : {}) }}
                                    id="name"
                                    type="text"
                                    placeholder="Name to be Displayed in Widget"
                                    className="text-base font-normal"
                                    {...register("brandName")}
                                />
                                {errors.brandName && <FormErrorMessage>{errors.brandName.message}</FormErrorMessage>}
                            </div>
                            <div className="flex flex-col gap-2">
                                <WidgetFormLabel htmlFor="status">
                                    Availability tag <span className="text-red-400">*</span>
                                </WidgetFormLabel>
                                <input
                                    style={{ ...inputStyle, ...(errors.statusTag?.message ? errMsgFocusStyle : {}) }}
                                    id="status"
                                    type="text"
                                    placeholder="Usually replies in a day"
                                    className="text-base font-normal"
                                    {...register("statusTag")}
                                />
                                {errors.statusTag && <FormErrorMessage>{errors.statusTag.message}</FormErrorMessage>}
                            </div>
                        </div>
                    </div>
                    <div className="px-6 my-6 flex flex-col gap-10">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center">
                                <Image width={50} height={50} src="https://assets.gallabox.com/gb-home/wa-chat-widget/customize.png" alt="CustomizeImage" />
                                <span className="ml-2 text-2xl font-bold text-gray-700">Customize Your Chat Widget</span>
                            </div>
                        </div>
                        <div>
                            <div className="flex flex-col gap-8">
                                <div className="flex flex-col gap-2">
                                    <WidgetFormLabel>
                                        Widget Style <span className="text-red-400">*</span>
                                    </WidgetFormLabel>
                                    <div className="flex gap-7">
                                        <div className="flex gap-2.5">
                                            <input style={{ transform: "scale(1.5)" }} type="radio" id="widgetStyle-v1" defaultChecked value="v1" {...register("widgetStyle")} />
                                            <label className="text-base font-normal" htmlFor="widgetStyle-v1">
                                                Expanded
                                            </label>
                                        </div>
                                        <div className="flex gap-2.5">
                                            <input
                                                style={{ transform: "scale(1.5)" }}
                                                type="radio"
                                                id="widgetStyle-v2"
                                                value="v2"
                                                onClick={() => setVersion("v2")}
                                                {...register("widgetStyle")}
                                            />
                                            <label className="text-base font-normal" htmlFor="widgetStyle-v2">
                                                Compact
                                            </label>
                                        </div>
                                    </div>
                                </div>
                                <div className="flex flex-col gap-2">
                                    <WidgetFormLabel>
                                        Widget Position <span className="text-red-400">*</span>
                                    </WidgetFormLabel>
                                    <div className="flex gap-7">
                                        <div className="flex gap-2.5">
                                            <input
                                                style={{ transform: "scale(1.5)" }}
                                                type="radio"
                                                id="buttonPosition-left"
                                                defaultChecked
                                                value="LEFT"
                                                {...register("buttonPosition")}
                                            />
                                            <label className="text-base font-normal" htmlFor="buttonPosition-left">
                                                Left
                                            </label>
                                        </div>
                                        <div className="flex gap-2.5">
                                            <input style={{ transform: "scale(1.5)" }} type="radio" id="buttonPosition-right" value="RIGHT" {...register("buttonPosition")} />
                                            <label className="text-base font-normal" htmlFor="buttonPosition-right">
                                                Right
                                            </label>
                                        </div>
                                    </div>
                                </div>
                                <div className="flex flex-row gap-3">
                                    <div className="w-40">
                                        <p className="text-sm">
                                            Margin Bottom (in px)<span className="text-red-400">*</span>
                                        </p>
                                        <input
                                            className="mt-1 h-10"
                                            type="number"
                                            id="widgetPositionMarginX"
                                            min={0}
                                            max={1000}
                                            {...register("widgetPositionMarginX", {
                                                setValueAs: (value) => parseFloat(value), // Convert string to number
                                            })}
                                        />
                                        {errors.widgetPositionMarginX && <FormErrorMessage>{errors.widgetPositionMarginX.message}</FormErrorMessage>}
                                    </div>
                                    <div className="w-40">
                                        <p className="text-sm">
                                            {selectedWidgetPosition === "LEFT" ? "Margin Left (in px)" : "Margin Right (in px)"}
                                            <span className="text-red-400">*</span>
                                        </p>
                                        <input
                                            className="mt-1 h-10"
                                            type="number"
                                            id="widgetPositionMarginY"
                                            min={0}
                                            max={1000}
                                            {...register("widgetPositionMarginY", {
                                                setValueAs: (value) => parseFloat(value), // Convert string to number
                                            })}
                                        />
                                        {errors.widgetPositionMarginY && <FormErrorMessage>{errors.widgetPositionMarginY.message}</FormErrorMessage>}
                                    </div>
                                </div>
                                <div className="flex flex-col gap-2 ">
                                    <Controller
                                        name="logo"
                                        control={control}
                                        render={({ field }) => (
                                            <FormLogo
                                                inputRef={(ref: HTMLInputElement) => {
                                                    field.ref(ref);
                                                    logoInputRef.current = ref;
                                                }}
                                                version={selectedWidgetStyle}
                                                error={errors.logo?.message}
                                                handleCallback={field.onChange}
                                            />
                                        )}
                                    />
                                    {errors.logo && <FormErrorMessage>{errors.logo.message}</FormErrorMessage>}
                                </div>
                                <div className="flex flex-col gap-2">
                                    <WidgetFormLabel htmlFor="welcomeText">
                                        Welcome Text <span className="text-red-400">*</span>
                                    </WidgetFormLabel>
                                    <textarea
                                        style={{ ...inputStyle, ...(errors.welcomeText?.message ? errMsgFocusStyle : {}) }}
                                        className="focus:outline-none text-base font-normal"
                                        id="welcomeText"
                                        placeholder="Welcome to Gallabox Enable engaging-conversation"
                                        maxLength={140}
                                        {...register("welcomeText")}
                                    />
                                    {errors.welcomeText && <FormErrorMessage>{errors.welcomeText.message}</FormErrorMessage>}
                                </div>
                                {selectedWidgetStyle === "v1" ? (
                                    <>
                                        <div className="flex flex-col gap-2">
                                            <WidgetFormLabel htmlFor="question">Question</WidgetFormLabel>
                                            <textarea
                                                style={inputStyle}
                                                className="focus:outline-none text-base font-normal"
                                                id="question"
                                                placeholder="I am looking for"
                                                maxLength={140}
                                                {...register("question")}
                                            />
                                            {errors.question && <FormErrorMessage>{errors.question.message}</FormErrorMessage>}
                                        </div>
                                        <div className="flex flex-col justify-center p-6 bg-blue-50 gap-2 border-b border-solid border-gray-100 rounded-xl">
                                            {fields.map((field, index) => (
                                                <div key={field.id} className="flex flex-col gap-2">
                                                    <WidgetFormLabel htmlFor={`option-${index}`}>{`Option ${index + 1}`}</WidgetFormLabel>
                                                    <div className="flex gap-2">
                                                        <input
                                                            style={inputStyle}
                                                            id={`option-${index}`}
                                                            type="text"
                                                            maxLength={40}
                                                            placeholder={optionsPlaceholder[index].content}
                                                            {...register(`options.${index}.content` as const)}
                                                        />
                                                        <div className="flex gap-1">
                                                            {fields.length === 1 ? null : (
                                                                <div className="flex items-center" onClick={() => removeOptions(index)}>
                                                                    <FaMinus style={optionButtonStyle} />
                                                                </div>
                                                            )}
                                                            {fields.length >= 3 ? null : (
                                                                <div className="flex items-center" onClick={() => addOptions(index)}>
                                                                    <FaPlus style={optionButtonStyle} />
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                    {errors.options?.[index]?.content && <FormErrorMessage>{errors.options?.[index]?.content?.message}</FormErrorMessage>}
                                                </div>
                                            ))}
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <div className="flex flex-col gap-2">
                                            <WidgetFormLabel htmlFor="customQuestion">
                                                Customer Question Text <span className="text-red-400">*</span>
                                            </WidgetFormLabel>
                                            <textarea
                                                style={{ ...inputStyle, ...(errors.customQuestion?.message ? errMsgFocusStyle : {}) }}
                                                className="focus:outline-none text-base font-normal"
                                                id="customQuestion"
                                                placeholder="Just visited your website! Would like to know more about it!"
                                                maxLength={140}
                                                {...register("customQuestion")}
                                            />
                                            {errors.customQuestion && <FormErrorMessage>{errors.customQuestion.message}</FormErrorMessage>}
                                        </div>
                                        <div className="flex flex-col gap-2">
                                            <WidgetFormLabel htmlFor="startChatBtnTxt">
                                                CTA <span className="text-red-400">*</span>
                                            </WidgetFormLabel>
                                            <input
                                                style={{ ...inputStyle, ...(errors.startChatBtnTxt?.message ? errMsgFocusStyle : {}) }}
                                                id="startChatBtnTxt"
                                                type="text"
                                                placeholder="Chat Now"
                                                className="text-base font-normal"
                                                maxLength={30}
                                                {...register("startChatBtnTxt")}
                                            />
                                            {errors.startChatBtnTxt && <FormErrorMessage>{errors.startChatBtnTxt.message}</FormErrorMessage>}
                                        </div>
                                    </>
                                )}
                                <div className="flex flex-col gap-2">
                                    <WidgetFormLabel htmlFor="triggerMessage">Trigger Message</WidgetFormLabel>
                                    <input
                                        style={{ ...inputStyle, ...(errors.triggerMsg?.message ? errMsgFocusStyle : {}) }}
                                        id="triggerMessage"
                                        type="text"
                                        placeholder="Message us"
                                        className="text-base font-normal"
                                        maxLength={26}
                                        {...register("triggerMsg")}
                                    />
                                    {errors.triggerMsg && <FormErrorMessage>{errors.triggerMsg.message}</FormErrorMessage>}
                                </div>
                            </div>
                            {/* @ts-expect-error react-google-recaptcha ref typing does not match the forwarded ref instance */}
                            <ReCAPTCHA ref={recaptchaRef} sitekey={sitekey} size="invisible" onChange={() => setRecaptchaError(null)} />
                            {recaptchaError && <span style={{ color: "red" }}>{recaptchaError}</span>}
                            <div className="flex justify-center my-10">
                                <button
                                    className="gb_chat_widget_generator_btn focus:outline-none bg-blue-500 rounded-md px-6 py-2.5 text-lg text-white font-semibold"
                                    type="submit"
                                >
                                    {buttonName}
                                </button>
                            </div>
                        </div>
                    </div>
                </form>
            </div>
            <div className="lg:w-1/2 w-full lg:flex-1 lg:flex md:flex sm:flex hidden flex-col items-center justify-start ">
                <div className="sticky top-10">
                    <div className="flex mt-8 justify-center">{control && <WhatsappWidgetPreview control={control} />}</div>
                    <div className="flex mt-8 justify-center">{control && <MinimizedWidget control={control} />}</div>
                </div>
            </div>
        </div>
    );
};

type WidgetFormLabelProps = React.DetailedHTMLProps<React.LabelHTMLAttributes<HTMLLabelElement>, HTMLLabelElement>;
export const WidgetFormLabel: React.FC<WidgetFormLabelProps> = (props) => {
    const { children, ...labelProps } = props;
    return (
        <label {...labelProps} className="text-xl font-medium text-gray-700">
            {children}
        </label>
    );
};

export const FormErrorMessage: React.FC<PropsWithChildren> = (props) => {
    const { children } = props;
    return <div className="text-sm text-red-500 mt-1 border-2 rounded-lg md:w-full border-gray-300 text-left">{children}</div>;
};

export default WidgetForm;
